import { Box, Stack } from '@chakra-ui/react';
import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { useLingui } from '@lingui/react/macro';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useMemo, useRef, useState } from 'react';

import { HostSessionCloseDialog } from '#modules/finder/features/posts/components/Post/Session/HostSessionCloseDialog';
import { DeleteAccountDialogBody } from '#modules/user/features/auth/components/DeleteAccountButton';
import { FollowButton } from '#modules/user/features/profile/components/Profile/FollowButton';
import { FilterOperator, OrderDirection } from '#features/api/types';
import { messages as enMessages } from '#features/i18n/locales/en/messages.po';
import { messages as ruMessages } from '#features/i18n/locales/ru/messages.po';
import { Content as NotificationsContent } from '#features/notifications/components/NotificationsPopover/Content';
import { notificationsQueryOptions } from '#features/notifications/model/notifications';
import {
  Button,
  ConfirmDialog,
  ConfirmDialogContent,
  HelpPopover,
  PageHeading,
  Panel,
} from '#features/ui';
import { DialogContent, DialogHeader, DialogRoot } from '#features/ui/components/Dialog';
import { PopoverRoot, PopoverTrigger } from '#features/ui/components/Popover';
import { toaster } from '#features/ui/components/Toaster';
import { LeaveVoiceForCallDialog } from '#features/voice/components/LeaveVoiceForCallDialog';

/** Isolated DEV fixture: callbacks only change local state, never product data. */
export const Route = createFileRoute('/{-$locale}/_layout/preview/confirmations')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: (search: Record<string, unknown>) => ({
    lang: search.lang === 'en' ? ('en' as const) : ('ru' as const),
    kind: String(search.kind ?? 'delete'),
    pending: search.pending === true || search.pending === 'true',
    long: search.long === true || search.long === 'true',
  }),
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
  component: PreviewRoute,
});

function PreviewRoute() {
  const { lang, kind, pending, long } = Route.useSearch();
  const i18n = useMemo(
    () => setupI18n({ locale: lang, messages: { en: enMessages, ru: ruMessages } }),
    [lang]
  );
  return (
    <I18nProvider i18n={i18n}>
      <Fixture
        key={`${lang}-${kind}-${pending}-${long}`}
        kind={kind}
        pending={pending}
        long={long}
      />
    </I18nProvider>
  );
}

function Fixture({ kind, pending, long }: { kind: string; pending: boolean; long: boolean }) {
  const { t } = useLingui();
  const isAccount = kind === 'account' || kind === 'account-error';
  const [open, setOpen] = useState(true);
  const [asking, setAsking] = useState(true);
  const [comment, setComment] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [reason, setReason] = useState<Parameters<typeof DeleteAccountDialogBody>[0]['reason']>();
  const displayName = long
    ? 'artevard131_очень_длинное_имя_игрока_без_пробелов_для_проверки_переноса'
    : 'artevard131';
  const close = () => setOpen(false);
  const notificationsClient = useMemo(() => {
    const client = new QueryClient();
    const queryDefaults = client.defaultQueryOptions.bind(client);
    client.defaultQueryOptions = (options) => ({
      ...queryDefaults(options),
      enabled: false,
      staleTime: Infinity,
    });
    const mutationDefaults = client.defaultMutationOptions.bind(client);
    client.defaultMutationOptions = (options) => ({
      ...mutationDefaults(options),
      mutationFn: async () => ({ __typename: 'Operation', status: 'OK' }) as never,
    });
    const orders = [{ direction: OrderDirection.DESC, field: 'updatedAt' }];
    const row = {
      id: 'qa-only',
      message: 'Teamplay',
      updatedAt: '2026-10-14T09:00:00Z',
      count: 1,
      isRead: false,
      userData: null,
    };
    client.setQueryData(notificationsQueryOptions({ offset: 0, limit: 20, orders }).queryKey, [
      row,
    ]);
    client.setQueryData(
      notificationsQueryOptions({
        offset: 0,
        limit: 20,
        orders,
        filters: [{ field: 'isRead', operator: FilterOperator.EQUALS, value: false, filters: [] }],
      }).queryKey,
      [row]
    );
    return client;
  }, []);
  return (
    <Box p={{ base: 4, md: 8 }}>
      <Stack gap={4}>
        <PageHeading>{t`settings.account.deleteAccount`}</PageHeading>
        <Panel p={4}>
          {kind === 'follow' ? (
            <FollowButton
              follow={{ state: 'FOLLOWING', friends: 2, followers: 3 }}
              isPending={pending}
              displayName={displayName}
              onFollow={() => {}}
              onUnfollow={close}
            />
          ) : (
            <Button ref={triggerRef} onClick={() => setOpen(true)}>
              {kind === 'host-complete' ? t`post:session.completeSession` : t`Delete`}
            </Button>
          )}
          {kind === 'help' ? (
            <HelpPopover accessibleLabel={t`settings.account.deleteAccount`}>
              {t`If you used Apple to sign in, after deleting your account check Settings → your name → Sign in with Apple. If Teamplay is still listed, select it and tap Delete.`}
            </HelpPopover>
          ) : null}
          {kind === 'notifications' ? (
            <QueryClientProvider client={notificationsClient}>
              <PopoverRoot
                open={notificationsOpen}
                onOpenChange={({ open: next }) => setNotificationsOpen(next)}
              >
                <PopoverTrigger asChild>
                  <Button>{t`notifications.title`}</Button>
                </PopoverTrigger>
                <NotificationsContent
                  isOpen={notificationsOpen}
                  onClose={() => setNotificationsOpen(false)}
                  onReopen={() => setNotificationsOpen(true)}
                />
              </PopoverRoot>
            </QueryClientProvider>
          ) : null}
        </Panel>
      </Stack>
      {kind === 'host-complete' ? (
        <QueryClientProvider client={notificationsClient}>
          <HostSessionCloseDialog
            action={open ? 'complete' : null}
            postId="qa-post"
            sessionId="qa-session"
            menuTriggerRef={triggerRef}
            onClose={close}
            onAction={() => {}}
          />
        </QueryClientProvider>
      ) : kind === 'voice' ? (
        <LeaveVoiceForCallDialog
          open={open}
          roomName="Marvel Rivals"
          onCancel={close}
          onConfirm={close}
        />
      ) : kind === 'nested' ? (
        <DialogRoot
          open={open}
          onOpenChange={({ open: next }) => setOpen(next)}
          initialFocusEl="body"
        >
          <DialogContent maxW="md">
            {asking ? (
              <ConfirmDialogContent
                title={t`post:session.room.leaveConfirmTitle`}
                description={t`post:session.room.leaveConfirmBody`}
                confirmLabel={t`Leave`}
                pending={pending}
                onCancel={() => setAsking(false)}
                onConfirm={close}
              />
            ) : (
              <>
                <DialogHeader title={t`Delete message?`} />
                <Button onClick={() => setAsking(true)}>{t`Leave`}</Button>
              </>
            )}
          </DialogContent>
        </DialogRoot>
      ) : !['follow', 'help', 'notifications'].includes(kind) ? (
        <ConfirmDialog
          open={open}
          title={
            isAccount
              ? t`settings.account.deleteAccount`
              : long
                ? t`Unfollow ${displayName}?`
                : t`Delete message?`
          }
          description={isAccount || long ? undefined : t`chat:delete.confirm.hint`}
          confirmLabel={long ? t`Unfollow` : t`Delete`}
          pending={pending}
          onCancel={close}
          onConfirm={
            kind === 'account-error'
              ? () =>
                  toaster.create({
                    id: 'verification-error',
                    title: t`common:error.generic`,
                    type: 'error',
                    duration: 60_000,
                  })
              : close
          }
        >
          {isAccount ? (
            <DeleteAccountDialogBody
              reason={reason}
              onReasonChange={setReason}
              comment={comment}
              onCommentChange={setComment}
              showAppleInstructions
            />
          ) : null}
        </ConfirmDialog>
      ) : null}
    </Box>
  );
}
